import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Title,
  Card,
  Flex,
  Divider,
  Checkbox,
  MultiSelect,
  Text,
} from '@mantine/core';
import {
  MRT_EditActionButtons,
  MRT_Row,
  MRT_TableInstance,
} from 'mantine-react-table';
import { Organization, Site, User } from 'interfaces';

interface UserFormModalProps {
  heading: string;
  row: MRT_Row<User>;
  table: MRT_TableInstance<User>;
  internalEditComponents: React.ReactNode[];
  organizations: Organization[];
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
  heading,
  row,
  table,
  internalEditComponents,
  organizations,
}) => {
  const [restrictUserSites, setRestrictUserSites] = useState<boolean>(false);
  const [selectedSiteIDs, setSelectedSiteIDs] = useState<string[]>([]);

  const selectedOrgID = row.getValue('organizationID') as string;

  const orgSites: Site[] = useMemo(() => {
    return organizations.find((org) => org.id === selectedOrgID)?.sites || [];
  }, [organizations, selectedOrgID]);

  useEffect(() => {
    const existingSites = row.original?.sites as Site[] | undefined;
    if (existingSites && existingSites.length > 0) {
      const existingSiteIDs = existingSites.map((s) => s.id);
      setRestrictUserSites(true);
      setSelectedSiteIDs(existingSiteIDs);
    }
  }, []);

  useEffect(() => {
    row._valuesCache = {
      ...row._valuesCache,
      allowedSiteIDs: restrictUserSites ? selectedSiteIDs : undefined,
    };
  }, [restrictUserSites, selectedSiteIDs, row]);

  return (
    <Stack
      data-testid={`user-form-modal-${heading
        .replace(/\s+/g, '-')
        .toLowerCase()}`}
    >
      <Title order={3} data-testid="user-form-title">
        {heading}
      </Title>
      <Card shadow="md" radius="md" padding="md" withBorder>
        <Stack>
          {internalEditComponents}
          <Divider label="Site Restrictions" labelPosition="center" my="sm" />
          <Checkbox
            label="Restrict user sites"
            checked={restrictUserSites}
            onChange={(e) => setRestrictUserSites(e.currentTarget.checked)}
            data-testid="restrictUserSitesCheckbox"
          />
          {restrictUserSites && (
            <MultiSelect
              label="Allowed Sites"
              data={orgSites.map((s) => ({ value: s.id, label: s.name }))}
              value={selectedSiteIDs}
              onChange={setSelectedSiteIDs}
              placeholder={
                selectedOrgID
                  ? 'Select one or more sites'
                  : 'Select an organization first'
              }
              disabled={!selectedOrgID || orgSites.length === 0}
              searchable
              clearable
              data-testid="restrictUserSitesMultiSelect"
            />
          )}
          {!selectedOrgID && (
            <Text size="xs" c="dimmed" data-testid="sites-help">
              Choose a role/organization to enable site selection.
            </Text>
          )}
        </Stack>
      </Card>
      <Flex justify="end">
        <MRT_EditActionButtons row={row} table={table} variant="text" />
      </Flex>
    </Stack>
  );
};

export default UserFormModal;
